import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import {
  Loader2, Sparkles, Wand2, AlertCircle, X, Check, FileText, Type, Package, Hand, RefreshCw, Copy,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

const TYPE_META = {
  page_seo: { label: 'PAGE META', icon: Type, tone: 'text-blue-300 border-blue-500/30 bg-blue-500/5' },
  blog_draft: { label: 'BLOG DRAFT', icon: FileText, tone: 'text-emerald-300 border-emerald-500/30 bg-emerald-500/5' },
  product_seo: { label: 'PRODUCT SEO', icon: Package, tone: 'text-amber-300 border-amber-500/30 bg-amber-500/5' },
  manual: { label: 'MANUAL', icon: Hand, tone: 'text-white/40 border-white/10 bg-white/[0.02]' },
};

function buildPrompt(data, products, categories) {
  const t = data.totals;
  const topQueries = data.queries.slice(0, 15).map(q =>
    `"${q.key}" — ${q.impressions} impressions, ${q.clicks} clicks, avg pos ${q.position.toFixed(1)}`
  ).join('\n');
  const topPages = data.pages.slice(0, 8).map(p => {
    let path; try { path = new URL(p.key).pathname; } catch { path = p.key; }
    return `${path} — ${p.impressions} impressions, ${p.clicks} clicks, avg pos ${p.position.toFixed(1)}`;
  }).join('\n');
  const productList = products.slice(0, 120).map(p =>
    `- ${p.name}${p.custom_slug ? ` (slug: ${p.custom_slug})` : ''}`
  ).join('\n');
  const categoryList = categories.length
    ? categories.map(c => `- key "${c.key}" / label "${c.label}"`).join('\n')
    : '(no category pages available)';

  return `You are a senior e-commerce SEO consultant for "Motion Reality", a UK premium motion racing simulator hardware store and cockpit booking platform in Bromborough (near Liverpool).

Here is the site's Google Search Console performance for the last 28 days:

TOTALS
- Clicks: ${t.clicks} (previous 28 days: ${t.prev_clicks})
- Impressions: ${t.impressions} (previous: ${t.prev_impressions})
- Average CTR: ${(t.ctr * 100).toFixed(2)}%
- Average position: ${t.position.toFixed(1)}

TOP QUERIES (by impressions)
${topQueries}

TOP PAGES (by impressions)
${topPages}

AVAILABLE PAGES (for "page_seo" actions — use these exact page_type values):
- page_type "home" — the homepage
- page_type "experiences" — the bookings / sim experiences page (also covers /booking)
- page_type "competition" — the monthly "win a sim racing experience" competition page at /competition (free entry, monthly random draw, UK residents)
- page_type "category" with category_key + category_label — a category landing page. Available categories:
${categoryList}

AVAILABLE PRODUCTS (for "product_seo" actions — product_name MUST match one of these exactly):
${productList}

Produce a CONCISE, PRIORITISED, immediately actionable SEO action plan. Return STRICT JSON matching the schema: an object with "summary" (one short paragraph) and "actions" (an array of action items). Each action item must have:
- title: short task name
- type: one of "page_seo" | "blog_draft" | "product_seo" | "manual"
- description: 1-2 sentences explaining the task and why, specific to the data above
- page_type, category_key, category_label: ONLY for type "page_seo"
- blog_title, blog_slug, blog_category (one of guides|tips|news|reviews), blog_excerpt, blog_keywords[]: ONLY for type "blog_draft"
- product_name: ONLY for type "product_seo" — must EXACTLY match a name from AVAILABLE PRODUCTS above

Rules:
- Use "page_seo" only for the homepage, the experiences/bookings page, or a listed category page. Never invent other page types.
- Use "product_seo" only for products that appear in the AVAILABLE PRODUCTS list. Match the name exactly.
- Use "blog_draft" for new blog post ideas targeting queries the site already gets impressions for.
- Use "manual" for anything that cannot be auto-applied (backlink outreach, building new landing pages, CRO/layout changes, schema markup).
- Keep the plan tight: 6-10 action items total. Prioritise quick wins first.
- No fluff, no generic disclaimers.`;
}

const ACTION_SCHEMA = {
  type: 'object',
  properties: {
    summary: { type: 'string' },
    actions: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          title: { type: 'string' },
          type: { type: 'string', enum: ['page_seo', 'blog_draft', 'product_seo', 'manual'] },
          description: { type: 'string' },
          page_type: { type: 'string' },
          category_key: { type: 'string' },
          category_label: { type: 'string' },
          blog_title: { type: 'string' },
          blog_slug: { type: 'string' },
          blog_category: { type: 'string' },
          blog_excerpt: { type: 'string' },
          blog_keywords: { type: 'array', items: { type: 'string' } },
          product_name: { type: 'string' },
        },
      },
    },
  },
};

function slugify(s) {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

// Stable signature for an action so we can recognise the same task across regenerated plans.
function actionKey(action) {
  switch (action.type) {
    case 'page_seo':
      return `page_seo:${action.page_type || ''}:${action.category_key || ''}`;
    case 'product_seo':
      return `product_seo:${(action.product_name || '').toLowerCase()}`;
    case 'blog_draft':
      return `blog_draft:${action.blog_slug || slugify(action.blog_title || '')}`;
    case 'manual':
    default:
      return `manual:${slugify(action.title || '')}`;
  }
}

export default function SeoActionPlan({ data }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null); // { summary, actions }
  const [error, setError] = useState('');
  const [rowStatus, setRowStatus] = useState([]); // per action: {status, message, persisted}
  const [doneMap, setDoneMap] = useState({}); // action_key -> detail (persisted completions)
  const { toast } = useToast();

  function setRow(i, patch) {
    setRowStatus((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], ...patch };
      return next;
    });
  }

  // Persist / clear a completion record so a task stays "done" across regenerations.
  async function upsertLog(action, detail) {
    const key = actionKey(action);
    try {
      const existing = await base44.entities.SeoActionLog.filter({ action_key: key });
      if (existing?.[0]?.id) await base44.entities.SeoActionLog.update(existing[0].id, { detail, title: action.title, type: action.type });
      else await base44.entities.SeoActionLog.create({ action_key: key, type: action.type, title: action.title, detail });
      setDoneMap((prev) => ({ ...prev, [key]: detail }));
    } catch (e) { /* log failures are non-fatal — the action itself still applied */ }
  }

  async function removeLog(action) {
    const key = actionKey(action);
    try {
      const existing = await base44.entities.SeoActionLog.filter({ action_key: key });
      if (existing?.[0]?.id) await base44.entities.SeoActionLog.delete(existing[0].id);
      setDoneMap((prev) => { const n = { ...prev }; delete n[key]; return n; });
    } catch (e) { /* non-fatal */ }
  }

  async function generate() {
    setOpen(true);
    setLoading(true);
    setError('');
    setPlan(null);
    setRowStatus([]);
    try {
      const [products, categories] = await Promise.all([
        base44.entities.Product.list('-created_date', 500).then(list =>
          (list || []).filter((p) => p.published !== false)
        ).catch(() => []),
        base44.entities.Category.list('sort_order').then(list =>
          (list || []).filter((c) => c.active)
        ).catch(() => []),
      ]);
      setProductsCache(products);

      // Load previously completed tasks so they show as DONE on a fresh plan.
      let doneInit = {};
      try {
        const logs = await base44.entities.SeoActionLog.list('-created_date', 200);
        doneInit = Object.fromEntries((logs || []).map((l) => [l.action_key, l.detail || 'Completed']));
      } catch (e) { /* entity may not exist yet — ignore */ }
      setDoneMap(doneInit);

      const res = await base44.integrations.Core.InvokeLLM({
        prompt: buildPrompt(data, products, categories),
        response_json_schema: ACTION_SCHEMA,
      });
      const parsed = res && typeof res === 'object' && !res.response ? res : null;
      const actions = Array.isArray(parsed?.actions) ? parsed.actions : [];
      if (!actions.length) throw new Error('AI returned no actionable items.');
      setPlan({ summary: parsed?.summary || '', actions });
      setRowStatus(actions.map((a) => {
        const done = doneInit[actionKey(a)];
        return done ? { status: 'done', message: done, persisted: true } : { status: 'idle' };
      }));
      toast({
        title: 'AI action plan ready',
        description: `${actions.length} prioritised tasks — click "Action with AI" on each.`,
      });
    } catch (e) {
      setError(e?.message || 'AI failed to generate an action plan. Try again.');
    } finally {
      setLoading(false);
    }
  }

  async function runPageSeo(action, i) {
    setRow(i, { status: 'running' });
    try {
      const res = await base44.functions.invoke('generatePageSeo', {
        pageType: action.page_type,
        categoryKey: action.category_key || '',
        categoryLabel: action.category_label || '',
      });
      const s = res.data?.suggestion;
      if (!s) throw new Error('No suggestion returned');
      let pageKey;
      if (action.page_type === 'home') pageKey = 'home';
      else if (action.page_type === 'experiences') pageKey = 'experiences';
      else if (action.page_type === 'competition') pageKey = 'competition';
      else if (action.page_type === 'category' && action.category_key) pageKey = `category:${action.category_key}`;
      else throw new Error('Invalid page type');
      const payload = {
        seo_title: s.seo_title || '',
        seo_description: s.seo_description || '',
        keywords: (s.keywords || []).join(', '),
      };
      const existing = await base44.entities.SiteSeo.filter({ page_key: pageKey });
      if (existing?.[0]?.id) await base44.entities.SiteSeo.update(existing[0].id, payload);
      else await base44.entities.SiteSeo.create({ page_key: pageKey, ...payload });
      setRow(i, { status: 'done', message: 'Meta saved', persisted: true });
      await upsertLog(action, 'Meta saved');
    } catch (e) {
      setRow(i, { status: 'error', message: e?.message || 'Failed' });
    }
  }

  async function runBlogDraft(action, i) {
    setRow(i, { status: 'running' });
    try {
      const prompt = `Write a complete UK-focused blog article for "Motion Reality", a UK premium motion racing simulator hardware store and booking platform in Bromborough (near Liverpool).

Title: ${action.blog_title}
Target keywords: ${(action.blog_keywords || []).join(', ')}
Excerpt: ${action.blog_excerpt || ''}

Write in engaging, expert-but-accessible British English. Structure with a strong intro, 3-4 H2 sections with short paragraphs, and a closing CTA encouraging readers to book a session or shop hardware at motionreality.co.uk. Return the article as clean HTML (use <h2>, <p>, <ul>/<li>, <strong>). No <h1> (the title is separate). Around 600-900 words.`;
      const res = await base44.integrations.Core.InvokeLLM({ prompt });
      const body = typeof res === 'string' ? res : (res?.response || res?.text || '');
      if (!body) throw new Error('AI returned no article body');
      const slug = action.blog_slug || slugify(action.blog_title);
      const created = await base44.entities.BlogPost.create({
        title: action.blog_title,
        slug,
        excerpt: action.blog_excerpt || '',
        body_html: body,
        category: ['guides', 'tips', 'news', 'reviews'].includes(action.blog_category) ? action.blog_category : 'guides',
        tags: action.blog_keywords || [],
        author_name: 'Motion Reality',
        published: false,
      });
      setRow(i, { status: 'done', message: 'Draft created — review in Admin → Blog', link: '/admin/blog', persisted: true });
      await upsertLog(action, 'Draft created — review in Admin → Blog');
      toast({ title: 'Blog draft created', description: `"${action.blog_title}" saved as unpublished.` });
    } catch (e) {
      setRow(i, { status: 'error', message: e?.message || 'Failed' });
    }
  }

  async function runProductSeo(action, i, products) {
    setRow(i, { status: 'running' });
    try {
      const target = (action.product_name || '').toLowerCase();
      const match = products.find(p => p.name?.toLowerCase() === target || p.custom_slug?.toLowerCase() === target)
        || products.find(p => p.name?.toLowerCase().includes(target));
      if (!match) throw new Error('Product not found — set SEO manually in Admin → Products');
      const res = await base44.functions.invoke('generateProductSeo', { productId: match.id });
      const s = res.data?.suggestion;
      if (!s) throw new Error('No suggestion returned');
      await base44.entities.Product.update(match.id, {
        seo_title: s.seo_title || '',
        seo_description: s.seo_description || '',
      });
      const detail = `SEO saved for "${match.name}"`;
      setRow(i, { status: 'done', message: detail, persisted: true });
      await upsertLog(action, detail);
    } catch (e) {
      setRow(i, { status: 'error', message: e?.message || 'Failed' });
    }
  }

  const [productsCache, setProductsCache] = useState([]);

  function copyManualPrompt(action) {
    const prompt = `Implement this SEO improvement for Motion Reality:\n\nTask: ${action.title}\nDetails: ${action.description}\n\nPlease build the necessary code/feature to action this.`;
    navigator.clipboard?.writeText(prompt).then(
      () => toast({ title: 'Copied to clipboard', description: 'Paste it into the Base44 chat to implement this task.' }),
      () => toast({ title: 'Copy failed', description: 'Copy the task manually from this card.', variant: 'destructive' }),
    );
  }

  async function markManualDone(action, i) {
    const detail = 'Marked as implemented';
    setRow(i, { status: 'done', message: detail, persisted: true });
    await upsertLog(action, detail);
    toast({ title: 'Task marked done', description: 'It will show as completed on future plans.' });
  }

  async function undoDone(action, i) {
    setRow(i, { status: 'idle', message: '' });
    await removeLog(action);
  }

  return (
    <>
      <div className="border border-primary/30 bg-primary/[0.03]">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-primary/20">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <div>
              <p className="text-primary text-[10px] font-bold tracking-widest">AI SEO ACTION PLAN</p>
              <h2 className="text-white font-bold text-sm mt-0.5">Let AI turn this data into a fix plan</h2>
            </div>
          </div>
          <button
            onClick={generate}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2.5 text-xs font-bold tracking-widest hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {loading ? 'ANALYSING DATA…' : 'FIX MY SEO WITH AI'}
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="bg-[#111115] border border-white/10 w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
            {/* Modal header */}
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-white/10 sticky top-0 bg-[#111115] z-10">
              <div className="flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-primary" />
                <div>
                  <p className="text-primary text-[10px] font-bold tracking-widest">AI SEO ACTION PLAN</p>
                  <h2 className="text-white font-bold text-sm mt-0.5">Prioritised tasks from your live search data</h2>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white shrink-0"><X className="w-5 h-5" /></button>
            </div>

            <div className="px-6 py-4">
              {loading && (
                <div className="py-16 flex flex-col items-center justify-center gap-3 text-white/40 text-sm">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  Reading your 28-day search data and writing a prioritised plan…
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2.5 px-4 py-3 bg-red-500/5 border border-red-500/30 mb-4">
                  <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <p className="text-red-300/80 text-xs">{error}</p>
                </div>
              )}

              {plan && !loading && (
                <>
                  <div className="flex items-start gap-2.5 border border-amber-500/30 bg-amber-500/5 p-3 mb-5">
                    <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                    <p className="text-amber-200/80 text-xs leading-relaxed">
                      <span className="text-amber-300 font-bold">Completed tasks are remembered.</span>{' '}
                      Page meta and product SEO are saved live; blog drafts are saved as unpublished posts in Admin → Blog (review then publish). Tasks you action stay marked <span className="font-bold">DONE</span> even after regenerating the plan — use <span className="font-bold">UNDO</span> to reset one. Tasks marked <span className="font-bold">MANUAL</span> can't be auto-applied — click <span className="font-bold">MANUAL — ASK BASE44 TO IMPLEMENT</span> to copy a ready-made instruction to paste into the Base44 chat, then <span className="font-bold">MARK AS DONE</span> once implemented. Once done, hit <span className="font-bold">SUBMIT SITE TO INDEXNOW</span> to re-crawl.
                    </p>
                  </div>

                  {plan.summary && (
                    <p className="text-white/60 text-sm leading-relaxed mb-5">{plan.summary}</p>
                  )}

                  <div className="flex items-start gap-2 border border-primary/20 bg-primary/[0.04] p-3 mb-5">
                    <Copy className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    <p className="text-white/70 text-xs leading-relaxed">
                      <span className="text-primary font-bold">Need a human touch?</span>{' '}
                      Tasks marked <span className="font-bold text-primary">MANUAL — ASK BASE44 TO IMPLEMENT</span> can't be auto-applied.
                      Click that button to <span className="font-bold">copy</span> a ready-made instruction, then paste it straight into the
                      Base44 chat — Base44 will build the fix for you.
                    </p>
                  </div>

                  <div className="space-y-3">
                    {plan.actions.map((action, i) => {
                      const meta = TYPE_META[action.type] || TYPE_META.manual;
                      const Icon = meta.icon;
                      const st = rowStatus[i] || { status: 'idle' };
                      return (
                        <div key={i} className="border border-white/10 bg-white/[0.02] p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className={`inline-flex items-center gap-1 text-[9px] font-bold tracking-widest border px-1.5 py-0.5 ${meta.tone}`}>
                                  <Icon className="w-3 h-3" /> {meta.label}
                                </span>
                              </div>
                              <h3 className="text-white font-bold text-sm">{action.title}</h3>
                              <p className="text-white/50 text-xs mt-1 leading-relaxed">{action.description}</p>
                              {action.blog_keywords?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {action.blog_keywords.map(k => (
                                    <span key={k} className="text-primary/70 text-[10px] bg-primary/5 border border-primary/20 px-1.5 py-0.5">{k}</span>
                                  ))}
                                </div>
                              )}
                              {st.message && (
                                <p className={`text-xs mt-2 ${st.status === 'done' ? 'text-green-400' : st.status === 'error' ? 'text-red-400' : 'text-white/40'}`}>
                                  {st.status === 'done' && <Check className="w-3 h-3 inline mr-1" />}
                                  {st.message}
                                </p>
                              )}
                            </div>
                            <div className="shrink-0 flex flex-col items-end gap-1.5">
                              {action.type === 'manual' ? (
                                st.status === 'done' ? (
                                  <>
                                    <span className="inline-flex items-center gap-1 text-green-400 text-[10px] font-bold tracking-widest border border-green-500/30 bg-green-500/5 px-3 py-2">
                                      <Check className="w-3.5 h-3.5" /> DONE
                                    </span>
                                    <button onClick={() => undoDone(action, i)} className="text-white/30 hover:text-white text-[9px] font-bold tracking-widest underline">UNDO</button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => copyManualPrompt(action)}
                                      title="Copy a ready-made instruction to paste into the Base44 chat"
                                      className="inline-flex items-center gap-1.5 text-primary text-[10px] font-bold tracking-widest border border-primary/30 bg-primary/5 hover:bg-primary/10 px-3 py-2 transition-colors text-left leading-tight"
                                    >
                                      <Copy className="w-3.5 h-3.5 shrink-0" /> MANUAL — ASK BASE44 TO IMPLEMENT
                                    </button>
                                    <button
                                      onClick={() => markManualDone(action, i)}
                                      title="Mark this task as implemented"
                                      className="text-green-400 hover:text-green-300 text-[9px] font-bold tracking-widest underline"
                                    >
                                      MARK AS DONE
                                    </button>
                                  </>
                                )
                              ) : st.status === 'done' ? (
                                <>
                                  <span className="inline-flex items-center gap-1 text-green-400 text-[10px] font-bold tracking-widest border border-green-500/30 bg-green-500/5 px-3 py-2">
                                    <Check className="w-3.5 h-3.5" /> DONE
                                  </span>
                                  {st.persisted && (
                                    <button onClick={() => undoDone(action, i)} className="text-white/30 hover:text-white text-[9px] font-bold tracking-widest underline">UNDO</button>
                                  )}
                                </>
                              ) : (
                                <button
                                  disabled={st.status === 'running'}
                                  onClick={() => {
                                    if (action.type === 'page_seo') runPageSeo(action, i);
                                    else if (action.type === 'blog_draft') runBlogDraft(action, i);
                                    else if (action.type === 'product_seo') runProductSeo(action, i, productsCache);
                                  }}
                                  className="inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white text-[10px] font-bold tracking-widest px-3 py-2 disabled:opacity-50 transition-colors"
                                >
                                  {st.status === 'running' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                                  {st.status === 'running' ? 'WORKING…' : 'ACTION WITH AI'}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex items-center justify-between gap-3 mt-6 pt-4 border-t border-white/10">
                    <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white text-xs font-bold tracking-widest">CLOSE</button>
                    <button
                      onClick={generate}
                      disabled={loading}
                      className="inline-flex items-center gap-2 border border-white/10 hover:border-primary/50 text-white/70 hover:text-white text-xs font-bold tracking-widest px-4 py-2.5 transition-colors"
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> REGENERATE PLAN
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}